/**
 * Floating dock: stacks every floating launcher on the page (ours and the
 * third-party apps') into one column in the bottom-right corner, and lifts the
 * column above the sticky add-to-cart bar while that bar is showing.
 *
 * To bring another app's launcher under the dock, add an entry to WIDGETS.
 * `find` returns the element the app positions itself with position:
 * fixed/absolute (reach into its shadowRoot if it has one), or null while the
 * app hasn't rendered yet.
 */
(() => {
  // Bottom to top.
  const WIDGETS = [
    { name: 'line', find: () => document.querySelector('.line-float-button') },
    { name: 'shopify-inbox', find: () => shadowQuery('shopify-chat', '.activator-nudge') },
    { name: 'smile', find: () => document.getElementById('smile-ui-lite-launcher-frame-container') },
    { name: 'clarity-agent', find: () => shadowQuery('#ads-agent-host', '.z-composer > .fixed') }
  ];

  const GAP = 12;
  // A widget taller than this is open (chat window, panel), so it is left
  // where the app puts it and doesn't take a place in the column.
  const MAX_LAUNCHER_HEIGHT = 160;
  // Apps inject their launchers late; keep looking for this long after load.
  const DISCOVERY_MS = 20000;

  const OVERRIDES = ['bottom', 'right', 'left', 'top', 'margin', 'translate'];
  const mdQuery = window.matchMedia((window.theme && theme.mediaQueries.md) || '(min-width: 769px)');
  const resizeObserver = new ResizeObserver(() => schedule());
  const watched = new WeakSet();
  let frame = null;

  function shadowQuery(hostSelector, selector) {
    const host = document.querySelector(hostSelector);
    return host && host.shadowRoot ? host.shadowRoot.querySelector(selector) : null;
  }

  /**
   * Gets the space taken at the bottom of the viewport by the sticky
   * add-to-cart bar, or 0 when it is hidden.
   * @returns {number} Height in px.
   */
  function stickyBarOffset() {
    const panel = document.querySelector('sticky-atc-panel');
    if (
      !panel ||
      panel.classList.contains('sticky-atc-panel--out') ||
      document.body.classList.contains('scrolled-to-bottom')
    ) return 0;

    const style = getComputedStyle(panel);
    if (style.display === 'none' || style.bottom !== '0px') return 0;
    return panel.offsetHeight + parseFloat(style.marginBottom);
  }

  function dock(el, bottom, edge) {
    if (!('floatingDock' in el.dataset) && getComputedStyle(el).transitionDuration === '0s') {
      el.style.setProperty('transition', 'bottom 0.3s ease');
    }
    el.style.setProperty('bottom', `calc(${bottom}px + env(safe-area-inset-bottom, 0px))`, 'important');
    el.style.setProperty('right', `${edge}px`, 'important');
    el.style.setProperty('left', 'auto', 'important');
    el.style.setProperty('top', 'auto', 'important');
    el.style.setProperty('margin', '0', 'important');
    el.style.setProperty('translate', 'none', 'important');
    el.dataset.floatingDock = '';
  }

  function release(el) {
    if (!('floatingDock' in el.dataset)) return;
    OVERRIDES.forEach((prop) => el.style.removeProperty(prop));
    delete el.dataset.floatingDock;
  }

  function watch(el) {
    if (watched.has(el)) return;
    watched.add(el);
    resizeObserver.observe(el);

    // Apps re-render inside their shadow roots; re-dock when they do.
    const root = el.getRootNode();
    if (root instanceof ShadowRoot && !watched.has(root)) {
      watched.add(root);
      new MutationObserver(schedule).observe(root, {
        childList: true,
        subtree: true,
        attributeFilter: ['class']
      });
    }
  }

  function layout() {
    frame = null;
    const edge = mdQuery.matches ? 24 : 16;
    let bottom = edge + stickyBarOffset();

    WIDGETS.forEach((widget) => {
      const el = widget.find();
      if (!el) return;
      watch(el);

      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height || rect.height > MAX_LAUNCHER_HEIGHT) {
        release(el);
        return;
      }

      dock(el, bottom, edge);
      bottom += rect.height + GAP;
    });
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(layout);
  }

  function init() {
    const bodyObserver = new MutationObserver(schedule);
    bodyObserver.observe(document.body, { childList: true, attributeFilter: ['class'] });

    const panel = document.querySelector('sticky-atc-panel');
    if (panel) new MutationObserver(schedule).observe(panel, { attributeFilter: ['class'] });

    window.addEventListener('resize', schedule);
    mdQuery.addEventListener('change', schedule);

    // Shadow roots get filled after their host lands in the body, so the body
    // observer alone can miss them.
    const poll = setInterval(schedule, 1000);
    setTimeout(() => clearInterval(poll), DISCOVERY_MS);

    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
